import { useCallback, useEffect, useState } from 'react';

const DEFAULT_SYMBOLS = ['AAPL', 'MSFT', 'NVDA', 'AMZN', 'META', 'TSLA', 'GOOGL', 'AMD'];
const STORAGE_KEY = 'goldmine-vision-watchlist';
const LEGACY_STORAGE_KEY = 'rust-wasm-trader-watchlist';
const MAX_SYMBOLS = 50;

export function useWatchlist() {
  const [symbols, setSymbols] = useState<string[]>(DEFAULT_SYMBOLS);

  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY) ?? localStorage.getItem(LEGACY_STORAGE_KEY);
      if (!stored) return;
      const parsed = JSON.parse(stored) as unknown;
      if (Array.isArray(parsed) && parsed.length) {
        const next = parsed.filter((value): value is string => typeof value === 'string').slice(0, MAX_SYMBOLS);
        setSymbols(next);
        localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      }
    } catch {
      // Ignore corrupt local storage and keep defaults.
    }
  }, []);

  const persist = useCallback((next: string[]) => {
    setSymbols(next);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  }, []);

  const addSymbol = useCallback((raw: string) => {
    const symbol = raw.trim().toUpperCase();
    if (!symbol || symbols.includes(symbol) || symbols.length >= MAX_SYMBOLS) return false;
    if (!/^[A-Z0-9.^=_-]{1,32}$/.test(symbol)) return false;
    persist([...symbols, symbol]);
    return true;
  }, [persist, symbols]);

  const removeSymbol = useCallback((symbol: string) => {
    if (symbols.length <= 1) return;
    persist(symbols.filter((item) => item !== symbol));
  }, [persist, symbols]);

  const resetWatchlist = useCallback(() => persist(DEFAULT_SYMBOLS), [persist]);

  return { symbols, addSymbol, removeSymbol, resetWatchlist, maxSymbols: MAX_SYMBOLS };
}
